import React, { useState, useEffect } from 'react';
import { useCompanies } from '../../hooks/useCompanies';
import CompanySuggestions from '../common/CompanySuggestions';
import FormField from '../common/FormField';

const CompanyForm = ({ company = null, onSubmit, onCancel, loading = false }) => {
  const { companies } = useCompanies();
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    industry: '',
    website: '',
    vatId: '',
    employeeCount: '',
    annualRevenue: '',
    logoUrl: '',
    linkedInUrl: '',
    notes: '',
    // Address
    street: '',
    city: '',
    postalCode: '',
    country: '',
    // Banking
    iban: '',
    swift: '',
    bankName: ''
  });
  const [errors, setErrors] = useState({});
  const [selectedExistingCompany, setSelectedExistingCompany] = useState(null);

  // Populate form if editing existing company
  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name || '',
        displayName: company.displayName || '',
        industry: company.industry || '',
        website: company.website || '',
        vatId: company.vatId || '',
        employeeCount: company.employeeCount || '',
        annualRevenue: company.annualRevenue || '',
        logoUrl: company.logoUrl || '',
        linkedInUrl: company.linkedInUrl || '',
        notes: company.notes || '',
        // Address
        street: company.address?.street || '',
        city: company.address?.city || '',
        postalCode: company.address?.postalCode || '',
        country: company.address?.country || '',
        // Banking
        iban: company.bankingDetails?.iban || '',
        swift: company.bankingDetails?.swift || '',
        bankName: company.bankingDetails?.bankName || ''
      });
    }
  }, [company]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Company name is required';
    }

    if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
      newErrors.website = 'Website must start with http:// or https://';
    }

    if (formData.linkedInUrl && !/^https?:\/\/.+/.test(formData.linkedInUrl)) {
      newErrors.linkedInUrl = 'LinkedIn URL must start with http:// or https://';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Build company object - only include fields with actual values
    const companyData = {
      name: formData.name.trim(),
      displayName: formData.displayName.trim() || formData.name.trim(),
    };

    // Add optional string fields only if they have values
    if (formData.industry.trim()) companyData.industry = formData.industry.trim();
    if (formData.website.trim()) companyData.website = formData.website.trim();
    if (formData.vatId.trim()) companyData.vatId = formData.vatId.trim();
    if (formData.logoUrl.trim()) companyData.logoUrl = formData.logoUrl.trim();
    if (formData.linkedInUrl.trim()) companyData.linkedInUrl = formData.linkedInUrl.trim();
    if (formData.notes.trim()) companyData.notes = formData.notes.trim();

    // Add number fields only if they have values
    if (formData.employeeCount) companyData.employeeCount = parseInt(formData.employeeCount);
    if (formData.annualRevenue) companyData.annualRevenue = parseFloat(formData.annualRevenue);

    // Add address if any field is filled
    if (formData.street || formData.city || formData.postalCode || formData.country) {
      companyData.address = {};
      if (formData.street.trim()) companyData.address.street = formData.street.trim();
      if (formData.city.trim()) companyData.address.city = formData.city.trim();
      if (formData.postalCode.trim()) companyData.address.postalCode = formData.postalCode.trim();
      if (formData.country.trim()) companyData.address.country = formData.country.trim();

      const formattedAddress = [
        formData.street,
        formData.city,
        formData.postalCode,
        formData.country
      ].filter(Boolean).join(', ');
      if (formattedAddress) companyData.address.formattedAddress = formattedAddress;
    }

    // Add banking if any field is filled
    if (formData.iban || formData.swift || formData.bankName) {
      companyData.bankingDetails = {};
      if (formData.iban.trim()) companyData.bankingDetails.iban = formData.iban.trim();
      if (formData.swift.trim()) companyData.bankingDetails.swift = formData.swift.trim();
      if (formData.bankName.trim()) companyData.bankingDetails.bankName = formData.bankName.trim();
    }

    onSubmit(companyData);
  };

  // Section header component for consistency
  const SectionHeader = ({ children }) => (
    <h3 className="text-sm font-semibold text-slate-700 dark:text-white uppercase tracking-wide">
      {children}
    </h3>
  );

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5">
      {/* Basic Information Section */}
      <div className="space-y-4">
        <SectionHeader>Basic Information</SectionHeader>

        {/* Company Name with duplicate prevention */}
        <CompanySuggestions
          value={formData.name}
          companies={companies}
          selectedCompany={selectedExistingCompany}
          onSelect={(selectedCompany) => {
            setSelectedExistingCompany(selectedCompany);
            if (selectedCompany && errors.name) {
              setErrors(prev => ({ ...prev, name: null }));
            }
          }}
          onChange={(value) => {
            setFormData(prev => ({ ...prev, name: value }));
            if (errors.name) {
              setErrors(prev => ({ ...prev, name: null }));
            }
          }}
          placeholder="Acme Corp"
          error={errors.name}
          label="Company Name"
          required
          disabled={loading}
          excludeId={company?.id}
        />

        {/* Warning when existing company is selected */}
        {selectedExistingCompany && (
          <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700">
            <p className="text-sm text-amber-800 dark:text-amber-200 mb-2">
              <strong>{selectedExistingCompany.name}</strong> already exists in your directory.
            </p>
            <button
              type="button"
              onClick={() => setSelectedExistingCompany(null)}
              className="text-sm text-amber-700 dark:text-amber-300 underline hover:no-underline"
            >
              Create anyway
            </button>
          </div>
        )}

        <FormField
          name="displayName"
          label="Display Name"
          value={formData.displayName}
          onChange={handleChange}
          placeholder="Short name (optional)"
          disabled={loading}
        />

        {/* Industry & Website Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            name="industry"
            label="Industry"
            value={formData.industry}
            onChange={handleChange}
            placeholder="Technology, Finance, etc."
            disabled={loading}
          />
          <FormField
            name="website"
            label="Website"
            type="url"
            value={formData.website}
            onChange={handleChange}
            placeholder="https://example.com"
            error={errors.website}
            disabled={loading}
          />
        </div>
      </div>

      {/* Business Details Section */}
      <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-[#233648]">
        <SectionHeader>Business Details</SectionHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            name="vatId"
            label="VAT/Tax ID"
            value={formData.vatId}
            onChange={handleChange}
            placeholder="IT12345678901"
            disabled={loading}
          />
          <FormField
            name="employeeCount"
            label="Employee Count"
            type="number"
            value={formData.employeeCount}
            onChange={handleChange}
            placeholder="50"
            min={0}
            disabled={loading}
          />
        </div>

        <FormField
          name="annualRevenue"
          label="Annual Revenue (EUR)"
          type="number"
          value={formData.annualRevenue}
          onChange={handleChange}
          placeholder="1000000"
          min={0}
          step={1000}
          disabled={loading}
        />
      </div>

      {/* Address Section */}
      <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-[#233648]">
        <SectionHeader>Address</SectionHeader>

        <FormField
          name="street"
          label="Street Address"
          value={formData.street}
          onChange={handleChange}
          placeholder="Via Roma 123"
          disabled={loading}
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormField
            name="city"
            label="City"
            value={formData.city}
            onChange={handleChange}
            placeholder="Milan"
            disabled={loading}
          />
          <FormField
            name="postalCode"
            label="Postal Code"
            value={formData.postalCode}
            onChange={handleChange}
            placeholder="20121"
            disabled={loading}
          />
          <FormField
            name="country"
            label="Country"
            value={formData.country}
            onChange={handleChange}
            placeholder="Italy"
            disabled={loading}
          />
        </div>
      </div>

      {/* Banking Section */}
      <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-[#233648]">
        <SectionHeader>Banking Details</SectionHeader>

        <FormField
          name="iban"
          label="IBAN"
          value={formData.iban}
          onChange={handleChange}
          placeholder="IT60 X054 2811 1010 0000 0123 456"
          disabled={loading}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            name="swift"
            label="SWIFT/BIC"
            value={formData.swift}
            onChange={handleChange}
            placeholder="UNCRITMM"
            disabled={loading}
          />
          <FormField
            name="bankName"
            label="Bank Name"
            value={formData.bankName}
            onChange={handleChange}
            placeholder="UniCredit"
            disabled={loading}
          />
        </div>
      </div>

      {/* Other Section */}
      <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-[#233648]">
        <SectionHeader>Additional Information</SectionHeader>

        <FormField
          name="logoUrl"
          label="Logo URL"
          type="url"
          value={formData.logoUrl}
          onChange={handleChange}
          placeholder="https://example.com/logo.png"
          disabled={loading}
        />

        <FormField
          name="linkedInUrl"
          label="LinkedIn URL"
          type="url"
          value={formData.linkedInUrl}
          onChange={handleChange}
          placeholder="https://linkedin.com/company/acme"
          error={errors.linkedInUrl}
          disabled={loading}
        />

        <FormField
          name="notes"
          label="Notes"
          as="textarea"
          rows={4}
          value={formData.notes}
          onChange={handleChange}
          placeholder="Add any additional notes..."
          disabled={loading}
        />
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 justify-end pt-4 border-t border-gray-200 dark:border-[#233648]">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-white bg-white dark:bg-[#192633] border border-gray-300 dark:border-[#324d67] rounded-lg hover:bg-gray-50 dark:hover:bg-[#233648] transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 text-sm font-bold text-white bg-primary hover:bg-blue-600 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {loading ? (
            <>
              <span className="material-symbols-outlined text-[18px] animate-spin">refresh</span>
              <span>Saving...</span>
            </>
          ) : (
            <span>{company ? 'Update Company' : 'Create Company'}</span>
          )}
        </button>
      </div>
    </form>
  );
};

export default CompanyForm;
